import React, { useState, useEffect } from "react";
import axios from "axios";
import { Drawer, Select, message, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { ButtonItem, InputNumberItem, SelectItem, SwitchItem } from "../../../../../Components/Items";

const { Option } = Select;

const TIPOS_RECORRIDO = [
  { value: "pasillo", label: "Pasillo" },
  { value: "cruce", label: "Cruce" },
  { value: "puerta", label: "Puerta" },
  { value: "rampa", label: "Rampa" },
  { value: "escalera", label: "Escalera" },
  { value: "elevador", label: "Elevador" },
  { value: "zona_restringida", label: "Zona restringida" },
  { value: "otro", label: "Otro" },
];

//Panel lateral para editar una conexión (tramo caminable) entre dos nodos del mapa.
const PanelPropiedadesConexion = ({ visible, onClose, conexion, headersToken, onGuardado, onEliminado }) => {
  const [tipoDireccion, setTipoDireccion] = useState("bidireccional");
  const [distanciaManual, setDistanciaManual] = useState(false);
  const [distanciaMetros, setDistanciaMetros] = useState(undefined);
  const [tipoRecorrido, setTipoRecorrido] = useState("pasillo");
  const [costoAdicional, setCostoAdicional] = useState(0);
  const [tiempoAdicionalSegundos, setTiempoAdicionalSegundos] = useState(0);
  const [bloqueada, setBloqueada] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (conexion) {
      setTipoDireccion(conexion.tipoDireccion || "bidireccional");
      setDistanciaManual(!!conexion.distanciaManual);
      setDistanciaMetros(conexion.distanciaMetros);
      setTipoRecorrido(conexion.tipoRecorrido || "pasillo");
      setCostoAdicional(conexion.costoAdicional || 0);
      setTiempoAdicionalSegundos(conexion.tiempoAdicionalSegundos || 0);
      setBloqueada(!!conexion.bloqueada);
    }
  }, [conexion]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await axios.post(
        "mapaAlmacen/editarConexionMapa",
        {
          _id: conexion._id,
          tipoDireccion,
          distanciaManual,
          distanciaMetros: distanciaManual ? distanciaMetros : undefined,
          tipoRecorrido,
          costoAdicional,
          tiempoAdicionalSegundos,
        },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success("Conexión actualizada correctamente");
        onGuardado();
      } else {
        message.error(res.data.error || "No se pudo actualizar la conexión");
      }
    } catch (error) {
      console.error("Error al actualizar la conexión:", error);
      message.error("No se pudo actualizar la conexión");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    try {
      const res = await axios.post("mapaAlmacen/eliminarConexionMapa", { _id: conexion._id }, { headers: headersToken });
      if (res.data.success === 2) {
        message.success("Conexión eliminada correctamente");
        onEliminado();
      } else {
        message.error(res.data.error || "No se pudo eliminar la conexión");
      }
    } catch (error) {
      console.error("Error al eliminar la conexión:", error);
      message.error("No se pudo eliminar la conexión");
    }
  };

  const cambiarBloqueo = async (valor) => {
    setBloqueada(valor);
    try {
      await axios.post("mapaAlmacen/bloquearConexionMapa", { _id: conexion._id, bloqueada: valor }, { headers: headersToken });
      onGuardado();
    } catch (error) {
      console.error("Error al cambiar el bloqueo de la conexión:", error);
    }
  };

  if (!conexion) return null;

  return (
    <Drawer title="Propiedades de la conexión" visible={visible} onClose={onClose} width={360}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Dirección</div>
        <SelectItem width="100%" value={tipoDireccion} onChange={setTipoDireccion}>
          <Option value="bidireccional">Bidireccional</Option>
          <Option value="unidireccional">Unidireccional (solo origen → destino)</Option>
        </SelectItem>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Tipo de recorrido</div>
        <SelectItem width="100%" value={tipoRecorrido} onChange={setTipoRecorrido}>
          {TIPOS_RECORRIDO.map((valorTipo) => (
            <Option key={valorTipo.value} value={valorTipo.value}>
              {valorTipo.label}
            </Option>
          ))}
        </SelectItem>
      </div>

      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <SwitchItem checked={distanciaManual} onChange={setDistanciaManual} />
        <span style={{ color: "#6b7280" }}>Capturar distancia manualmente</span>
      </div>

      {distanciaManual ? (
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Distancia (metros)</div>
          <InputNumberItem style={{ width: "100%" }} min={0} value={distanciaMetros} onChange={setDistanciaMetros} />
        </div>
      ) : (
        <div style={{ marginBottom: 12, color: "#8a93a6", fontSize: 12 }}>
          Distancia calculada automáticamente: <strong>{Number(distanciaMetros || 0).toFixed(2)} m</strong>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Costo adicional</div>
        <InputNumberItem style={{ width: "100%" }} min={0} value={costoAdicional} onChange={setCostoAdicional} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Tiempo adicional (segundos)</div>
        <InputNumberItem style={{ width: "100%" }} min={0} value={tiempoAdicionalSegundos} onChange={setTiempoAdicionalSegundos} />
      </div>

      <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
        <SwitchItem checked={bloqueada} onChange={cambiarBloqueo} />
        <span style={{ color: "#6b7280" }}>Bloqueada (excluida de las rutas)</span>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Popconfirm title="¿Eliminar esta conexión?" onConfirm={eliminar} okText="Eliminar" cancelText="Cancelar">
          <ButtonItem danger icon={<DeleteOutlined />}>
            Eliminar
          </ButtonItem>
        </Popconfirm>
        <ButtonItem type="primary" loading={guardando} onClick={guardar}>
          Guardar
        </ButtonItem>
      </div>
    </Drawer>
  );
};

export default PanelPropiedadesConexion;
